// src/components/sections/BusinessProfileSection.js

import React, { useState, useEffect } from 'react';
import { 
  Building2, Mail, Phone, Globe, MapPin, 
  Camera, Upload, Check, AlertCircle 
} from 'lucide-react';
import { colors } from '../../styles/designSystem';
import { GradientButton } from '../ui/GradientButton';
import { useTranslation } from 'react-i18next';
import api from '../../api'; // Changed import from 'axios' to 'api'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const BusinessProfileSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    description: '',
    sector: '',
    logo: null,
    logoUrl: '',
  });

  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        const response = await api.get('/business-profile');
        if (response.data) {
          setFormData({
            name: response.data.name || '',
            email: response.data.email || '',
            phone: response.data.phone || '',
            website: response.data.website || '',
            address: response.data.address || '',
            description: response.data.description || '',
            sector: response.data.sector || '',
            logo: null,
            logoUrl: response.data.logo ? `${API_BASE_URL}${response.data.logo}` : '',
          });
        }
      } catch (err) {
        console.error('Error fetching business profile frontend:', err);
      }
    };
  
    const fetchSectors = async () => {
      try {
        const response = await api.get('/sectors');
        setSectors(response.data);
      } catch (err) {
        console.error('Error fetching sectors:', err);
      }
    };
  
    fetchBusinessProfile();
    fetchSectors();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        logo: file,
        logoUrl: URL.createObjectURL(file),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('sector', formData.sector);
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      if (formData.logo) {
        formDataToSend.append('logo', formData.logo);
      }

      const response = await api.post('/business-profile', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage(response.data.message);
    } catch (err) {
      console.error('Error saving business profile:', err);
      setError(err.response?.data?.error || t('BusinessProfileSection.alerts.error_message'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div>
        <h2 className={`${colors.text.heading} text-2xl font-semibold mb-2`}>
          {t('BusinessProfileSection.title')}
        </h2>
        <p className={`${colors.text.body}`}>
          {t('BusinessProfileSection.description')}
        </p>
      </div>

      {/* Alert Messages */}
      {(message || error) && (
        <div className={`p-4 rounded-xl ${
          message ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        }`}>
          <div className="flex items-center">
            {message ? (
              <Check className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <p>{message || error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Upload Section */}
        <div className="flex items-start space-x-6">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                {formData.logo ? (
                  <img 
                    src={formData.logoUrl} 
                    alt="Business logo" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  formData.logoUrl ? (
                    <img 
                      src={formData.logoUrl} 
                      alt="Business logo" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Camera className="w-8 h-8 text-gray-400" />
                  )
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer">
                <Upload className="w-4 h-4 text-gray-600" />
                <input
                  type="file"
                  className="hidden"
                  onChange={handleLogoChange}
                  accept="image/*"
                />
              </label>
            </div>
          </div>

          <div className="flex-grow">
            <h3 className="text-lg font-medium mb-1">{t('BusinessProfileSection.form.logo.label')}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('BusinessProfileSection.form.logo.description')}
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('BusinessProfileSection.form.name.label')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building2 className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="pl-10 w-full rounded-xl border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('BusinessProfileSection.form.name.placeholder')}
                required
              />
            </div>
          </div>

          {/* Email Address */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('BusinessProfileSection.form.email.label')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="pl-10 w-full rounded-xl border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('BusinessProfileSection.form.email.placeholder')}
                required
              />
            </div>
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('BusinessProfileSection.form.phone.label')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="pl-10 w-full rounded-xl border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('BusinessProfileSection.form.phone.placeholder')}
                required
              />
            </div>
          </div>

          {/* Website (Optional) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('BusinessProfileSection.form.website.label')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Globe className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="pl-10 w-full rounded-xl border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('BusinessProfileSection.form.website.placeholder')}
              />
            </div>
          </div>

          {/* Sector */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('BusinessProfileSection.form.sector.label')}
            </label>
            <div className="relative">
              <select
                name="sector"
                value={formData.sector}
                onChange={handleInputChange}
                className="pl-3 pr-10 py-2 w-full rounded-xl border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                required
              >
                <option value="">{t('BusinessProfileSection.form.sector.placeholder')}</option>
                {sectors.map(sector => (
                  <option key={sector._id} value={sector._id}>{sector.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description (Optional) */}
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('BusinessProfileSection.form.description.label')}
            </label>
            <div className="relative">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="pl-3 w-full rounded-xl border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('BusinessProfileSection.form.description.placeholder')}
              />
            </div>
          </div>

          {/* Business Address */}
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('BusinessProfileSection.form.address.label')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-5 w-5 text-gray-400" />
              </div>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="pl-10 w-full rounded-xl border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('BusinessProfileSection.form.address.placeholder')}
                required
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="px-6 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {t('BusinessProfileSection.form.buttons.cancel')}
          </button>
          <GradientButton
            type="submit"
            disabled={loading}
            className="px-6 py-2"
          >
            {loading ? t('BusinessProfileSection.form.buttons.saving') : t('BusinessProfileSection.form.buttons.save')}
          </GradientButton>
        </div>
      </form>
    </div>
  );
};

export default BusinessProfileSection;
